import React, { useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';

interface RepoEntity {
  metadata: {
    name: string;
    annotations?: Record<string, string>;
  };
  spec?: {
    system?: string;
  };
}

interface RepoOption {
  name: string;
  projectSlug?: string;
}

export const CatalogRepoPicker = ({
  onChange,
  errors,
  rawErrors,
  required,
  help,
  disabled,
  schema: { title = 'Repository', description },
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const uiOptions = (uiSchema?.['ui:options'] as Record<string, unknown>) ?? {};
  const systemField = (uiOptions.systemField as string) ?? 'ref';

  const systemRef: string = (formContext as any)?.formData?.[systemField] ?? '';

  const backendUrl = config.getString('backend.baseUrl');

  const { value, loading, error } = useAsync(async (): Promise<RepoOption[]> => {
    if (!systemRef) return [];

    const params = new URLSearchParams();
    params.append('filter', 'kind=Component');
    params.set('fields', 'metadata.name,metadata.annotations,spec.system');
    params.set('limit', '1000');

    const res = await fetchApi.fetch(
      `${backendUrl}/api/catalog/entities/by-query?${params.toString()}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch repositories: ${res.status} ${text}`);
    }

    const data = await res.json();
    const allRepos: RepoEntity[] = data.items ?? [];

    return allRepos
      .filter(r => r.spec?.system === systemRef)
      .filter(r => !!r.metadata.annotations?.['github.com/project-slug'])
      .map(r => ({
        name: r.metadata.name,
        projectSlug: r.metadata.annotations!['github.com/project-slug'],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [systemRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const repoOptions = useMemo(() => value ?? [], [value]);

  const selectedRepo = useMemo(() => {
    if (!formData) return null;
    return repoOptions.find(o => o.projectSlug === formData) ?? null;
  }, [formData, repoOptions]);

  const getOptionLabel = (o: RepoOption) => `${o.name} (${o.projectSlug})`;

  if (!systemRef) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth disabled>
          <Autocomplete
            options={[]}
            disabled
            getOptionLabel={getOptionLabel}
            renderInput={params => (
              <TextField {...params} label={title} placeholder="Select a system first" />
            )}
          />
        </FormControl>
      </ScaffolderField>
    );
  }

  if (loading) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth>
          <Autocomplete
            options={[]}
            loading
            disabled
            getOptionLabel={getOptionLabel}
            renderInput={params => (
              <TextField
                {...params}
                label={title}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <CircularProgress color="inherit" size={20} />
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControl>
      </ScaffolderField>
    );
  }

  if (error) {
    return (
      <ScaffolderField
        rawErrors={[...(rawErrors || []), `Failed to load repositories: ${error.message}`]}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <Alert severity="error">
          Failed to load repositories: {error.message}
        </Alert>
      </ScaffolderField>
    );
  }

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      errors={errors}
      required={required}
      help={help}
      rawDescription={description}
      disabled={disabled}
    >
      <FormControl fullWidth error={rawErrors && rawErrors.length > 0}>
        <Autocomplete
          options={repoOptions}
          value={selectedRepo}
          getOptionLabel={getOptionLabel}
          getOptionSelected={(o: RepoOption, v: RepoOption) => o.name === v.name}
          onChange={(_, newValue) => {
            onChange((newValue as RepoOption | null)?.projectSlug ?? '');
          }}
          disabled={disabled || repoOptions.length === 0}
          renderInput={params => (
            <TextField
              {...params}
              label={title}
              placeholder="Search or select a repository..."
            />
          )}
          noOptionsText="No repositories found for this system"
        />
        {help && <FormHelperText>{help}</FormHelperText>}
      </FormControl>
    </ScaffolderField>
  );
};
